import {
    Component,
    Input,
    OnInit,
    ViewChild,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { DatepickerComponent } from "@components/datepicker/datepicker.component";
import { MultiSelectorComponent } from "@components/multi-selector/multi-selector.component";
import { TogglePanelComponent } from "@components/toggle-panel/toggle-panel.component";
import { TreeSelectorComponent } from "@components/tree-selector/tree-selector.component";
import { TranslateDirective, TranslatePipe } from "@ngx-translate/core";
import _ from "lodash";
import { BusyService } from "~app/common/components/busy/busy.service";
import { ITypeAheadResult } from "~app/common/models/type-ahead-result.model";
import { DirtyFormService } from "~app/common/services/dirty-form.service";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { WebinstallningarService } from "~app/common/services/webinstallningar/webinstallningar.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ValidationErrorLevels } from "~app/common/validation/ValidationErrorLevels";
import { ValidationScopes } from "~app/common/validation/ValidationScopes";
import { ValidationContext } from "~app/common/validation/validationContext";
import { DirtyTabComponent } from "~app/features/regelverk-viol/dirtyTabComponent";
import { AreaSelectionType, IJlpResultSettings, INamedSokfilter } from "../../common/models/jlp-model";
import { JlpProduktionsomradenSearchModel } from "../../common/models/jlp-produktionsomrade-model";
import { GridPeriod, IOrderProduktionsomradeSearchResponse } from "../../common/models/justeraLeveransplanModel";
import { JlpPeriodService } from "../../common/services/jlp-period.service";
import { JusteraLeveransplanFilterService } from "../../common/services/justera-leveransplan-filter.service";
import { JusteraLeveransplanService } from "../../common/services/justera-leveransplan.service";
import { ProduktionsomradenValidation } from "../../common/validation/jlp-validation";
import { JlpSavedSearchesComponent } from "../../components/jlp-saved-searches/jlp-saved-searches.component";
import { JlpProduktionsomradenResultComponent } from "./jlp-produktionsomraden-result/jlp-produktionsomraden-result.component";

@Component({
    selector: "rask-jlp-produktionsomraden",
    templateUrl: "./jlp-produktionsomraden.component.html",
    styleUrls: ["./jlp-produktionsomraden.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TogglePanelComponent,
        JlpSavedSearchesComponent,
        FormsModule,
        ReactiveFormsModule,
        TreeSelectorComponent,
        MultiSelectorComponent,
        DatepickerComponent,
        ButtonComponent,
        JlpProduktionsomradenResultComponent,
        TranslateDirective,
        TranslatePipe
    ]
})
export class JlpProduktionsomradenComponent extends DirtyTabComponent implements OnInit {

    readonly WIKSparade = "JusteraLeveransplan.produktionsomradefilter.sparade";
    readonly name = "produktionsomraden";

    basdata: any = null;
    @Input() canEdit: boolean;
    currentData: IOrderProduktionsomradeSearchResponse;
    model: JlpProduktionsomradenSearchModel;
    noOrderrows = false;
    period: GridPeriod;
    produktionsomradenForm = new UntypedFormGroup({
        kunder: new UntypedFormControl([]),
        omraden: new UntypedFormControl([]),
        mottagningsplatser: new UntypedFormControl([]),
        handelssortimentsgrupper: new UntypedFormControl([]),
        periodFrom: new UntypedFormControl(),
        periodTom: new UntypedFormControl()
    });
    @ViewChild(JlpProduktionsomradenResultComponent) results: JlpProduktionsomradenResultComponent;
    senastAktivaProduktionsomraden: number[];
    settings: IJlpResultSettings;
    showResults = false;
    sokningar: INamedSokfilter[];
    triggerSparaFilter: any;
    validationContext: ValidationContext;

    private readonly WIK = "JusteraLeveransplan.produktionsomradefilter";
    private readonly busyService = inject(BusyService);
    private readonly dirtyFormServiceRef: DirtyFormService;
    private readonly formBinder = inject(FormBinderService);
    private readonly jlpPeriodService = inject(JlpPeriodService);
    private readonly jlpService = inject(JusteraLeveransplanService);
    private readonly justeraLeveransplanFilterService = inject(JusteraLeveransplanFilterService);
    private readonly webinstallningarService = inject(WebinstallningarService);

    constructor() {
        const dirtyFormService = inject(DirtyFormService);

        super(dirtyFormService);
        this.dirtyFormServiceRef = dirtyFormService;
    }

    canSave() {
        this.results.isDirty();
    }

    formatter = (result: ITypeAheadResult) => result ? result.namn : undefined;

    isDirtyCheck(): boolean {
        if (this.results === undefined) {
            return false;
        }
        return this.results.isDirty();
    }

    ngOnInit() {
        this.jlpService.getJusteraLeveransplanBasdata().subscribe((basdata: any) => {
            this.model = _.merge(new JlpProduktionsomradenSearchModel(), this.webinstallningarService.get<JlpProduktionsomradenSearchModel>(this.WIK));
            this.basdata = this.justeraLeveransplanFilterService.setFilter(basdata, this.model, this.justeraLeveransplanFilterService.treeTypes.handelssortiment);
            this.basdata = this.justeraLeveransplanFilterService.setFilter(basdata, this.model, this.justeraLeveransplanFilterService.treeTypes.produktionsomrade);
            this.setForm();
            this.validationContext = ProduktionsomradenValidation.init(this.model);
        });
    }

    outputFormatter = (result: ITypeAheadResult) => result ? [result] : undefined;

    reset() {
        this.produktionsomradenForm.controls.kunder.setValue([]);
        this.produktionsomradenForm.controls.omraden.setValue([]);
        this.produktionsomradenForm.controls.mottagningsplatser.setValue([]);
        this.produktionsomradenForm.controls.handelssortimentsgrupper.setValue([]);
        this.produktionsomradenForm.controls.periodFrom.setValue(null);
        this.produktionsomradenForm.controls.periodTom.setValue(null);
        this.setModel(this.produktionsomradenForm.value);
        this.basdata = this.justeraLeveransplanFilterService.setFilter(this.basdata, this.model, this.justeraLeveransplanFilterService.treeTypes.handelssortiment);
        this.basdata = this.justeraLeveransplanFilterService.setFilter(this.basdata, this.model, this.justeraLeveransplanFilterService.treeTypes.produktionsomrade);
        this.setForm();
    }

    restoreChanges() {
        this.search();
    }

    saveChanges() {
        this.results.save();
    }

    saveSearchParams() {
        this.produktionsomradenForm.value.handelssortimentsgrupper = this.justeraLeveransplanFilterService.getSelectedHandelssortiment(this.basdata.handelssortiment);
        this.produktionsomradenForm.value.omraden = this.justeraLeveransplanFilterService
            .getSelectedAreas(this.basdata.omraden, this.justeraLeveransplanFilterService.areaType.marknadsomrade);

        if (ObjectUtils.hasValue(this.produktionsomradenForm.get("periodFrom").value)) {
            this.produktionsomradenForm.value.periodFrom = this.jlpPeriodService.addFirstDayOfMonth(this.produktionsomradenForm.get("periodFrom").value);
        }

        if (ObjectUtils.hasValue(this.produktionsomradenForm.get("periodTom").value)) {
            this.produktionsomradenForm.value.periodTom = this.jlpPeriodService.getLastDayOfMonth(this.produktionsomradenForm.get("periodTom").value);
        }

        this.triggerSparaFilter = Object.assign({}, this.produktionsomradenForm.value);
    }

    search() {
        const searchFunc = () => {
            this.noOrderrows = false;
            this.currentData = null;
            this.setModel(this.produktionsomradenForm.value);
            this.validationContext.scope(ValidationScopes.sok, ValidationErrorLevels.error).subscribe(scope => {
                if (this.validationContext.validate(true)) {
                    this.produktionsomradenForm.value.handelssortimentsgrupper = this.justeraLeveransplanFilterService.getSelectedHandelssortiment(this.basdata.handelssortiment);
                    this.produktionsomradenForm.value.omraden = this.justeraLeveransplanFilterService
                        .getSelectedAreas(this.basdata.omraden, this.justeraLeveransplanFilterService.areaType.marknadsomrade);

                    this.produktionsomradenForm.value.periodFrom = this.jlpPeriodService.addFirstDayOfMonth(this.produktionsomradenForm.get("periodFrom").value);
                    this.produktionsomradenForm.value.periodTom = this.jlpPeriodService.getLastDayOfMonth(this.produktionsomradenForm.get("periodTom").value);

                    this.webinstallningarService.set(this.WIK, this.produktionsomradenForm.value);
                    const query = this.jlpService.searchForProduktionsomrade(this.produktionsomradenForm.value);
                    this.busyService.addWork(query).subscribe((res: IOrderProduktionsomradeSearchResponse) => {
                        this.senastAktivaProduktionsomraden = this.produktionsomradenForm.value.omraden
                        // eslint-disable-next-line sonarjs/no-nested-functions
                            .filter((p: any) => p.typ === AreaSelectionType.produktionsomrade)
                        // eslint-disable-next-line sonarjs/no-nested-functions
                            .map((p: any) => p.id);

                        this.showResults = true;
                        if (res.kundorderrader.length === 0) {
                            this.noOrderrows = true;
                        } else {
                            this.settings = {
                                passeradeAntalArbetsdagarInnevarandeManad: res.passeradeAntalArbetsdagarInnevarandeManad,
                                totaltAntalArbetsdagarInnevarandeManad: res.totaltAntalArbetsdagarInnevarandeManad,
                                periodFrom: this.produktionsomradenForm.get("periodFrom").value,
                                periodTom: this.produktionsomradenForm.get("periodTom").value
                            };
                            this.period = this.jlpPeriodService.getPeriods(this.settings.periodFrom, this.settings.periodTom);
                            this.currentData = res;
                        }
                    });
                }
            });
        };

        if (this.isDirtyCheck()) {
            this.dirtyFormServiceRef.dirtyFormModal(
                () => searchFunc(),
                () => {
                    this.saveChanges();
                    searchFunc();
                },
                () => undefined);
        } else {
            searchFunc();
        }
    }

    searchForCustomer = (searchString: string) => this.jlpService.searchForKundTypeAhead(searchString);

    searchForMottagningsplatsTypeAhead = (searchString: string) => this.jlpService.searchForMottagningsplatsTypeAhead(searchString, null);

    setSokning(namedSokFilter: INamedSokfilter) {
        this.reset();
        if (namedSokFilter) {
            this.setModel(namedSokFilter);
            this.basdata.omraden = null;
            this.basdata = this.justeraLeveransplanFilterService.setFilter(this.basdata, this.model, this.justeraLeveransplanFilterService.treeTypes.handelssortiment);
            this.basdata = this.justeraLeveransplanFilterService.setFilter(this.basdata, this.model, this.justeraLeveransplanFilterService.treeTypes.produktionsomrade);
            this.setForm();
        }
    }

    private setForm() {
        if (!ObjectUtils.hasValue(this.produktionsomradenForm)) {
            this.produktionsomradenForm = this.formBinder.CreateGroup(this.model);
        } else {
            this.produktionsomradenForm.controls.kunder.setValue(this.model.kunder);
            this.produktionsomradenForm.controls.mottagningsplatser.setValue(this.model.mottagningsplatser);
            this.produktionsomradenForm.controls.periodFrom.setValue(this.model.periodFrom);
            this.produktionsomradenForm.controls.periodTom.setValue(this.model.periodTom);
            this.produktionsomradenForm.controls.handelssortimentsgrupper.setValue(this.model.handelssortimentsgrupper);
            this.produktionsomradenForm.controls.omraden.setValue(this.model.omraden);
        }
    }

    private setModel(values: any) {
        this.model.kunder = values.kunder;
        this.model.mottagningsplatser = values.mottagningsplatser;
        this.model.periodFrom = values.periodFrom;
        this.model.periodTom = values.periodTom;
        this.model.omraden = values.omraden ?? [];
        this.model.handelssortimentsgrupper = values.handelssortimentsgrupper ?? [];
    }
}
